import { Alert, AlertDescription, AlertTitle, Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { errorMessage } from '@/domains/api';
import { authApi } from '../api';
import { TestInboxNote } from '../components/test-inbox-field';
import { CATALOGUE_ROUTE, LOGIN_ROUTE } from '../constants';
import type { IEnteredNumber } from '../storage';
import type { RegistrationCaller } from '../types';
import { DOOR, enteredNumberText, expiryText } from './registration-copy';
import { HelpLink, StepHeading, useStepAction } from './step-parts';

/**
 * The ends of a run: the account it made, and the four that are not an account — a person's
 * number another account holds, a firm's or a team's refused over its number, finished already,
 * and closed. Each is drawn from what survey-api answered and says nothing it did not.
 */

/**
 * Page 09, "Check your email" — the anonymous completion's 201 (SR-487 decision 9). The account
 * exists and nobody is signed in: survey-api queued an invitation to sign in at Invent's IdP for
 * the address — to the test inbox where one was named — and the answer says when it expires
 * (`invitationExpiresAt`), counted here in whole days; with no expiry, nothing was queued to
 * count, and the line is left out.
 *
 * **"Send it again"** asks for the invitation once more (`POST /auth/idp-invitations/requests`,
 * the address and the same test inbox), which survey-api answers 202 whatever it then sends — so
 * the line after it says it was asked, as far as this side can know. A wrong address is staff's to
 * correct (Q2): the line says so and links to Help, the one way to reach Veritant (Q3). No "Sign
 * in" button: the wireframe has none, and the invitation's link is the way in.
 */
export function CheckEmail({
  email,
  deliverTo,
  expiresAt
}: {
  email: string;
  deliverTo?: string;
  expiresAt: Nullable<string>;
}) {
  const { pending, error, run } = useStepAction();
  const [sent, setSent] = React.useState(false);
  const expiry = expiryText(expiresAt);
  const words = DOOR.checkEmail;

  return (
    <>
      <StepHeading
        title={words.title}
        lede={
          <>
            We have sent a link to <strong className="break-words">{email}</strong>.
            {expiry && (
              <>
                <br />
                {expiry}
              </>
            )}
          </>
        }
      />
      {deliverTo && (
        <TestInboxNote className="mb-6">
          this environment sends it to <strong className="break-words">{deliverTo}</strong>{' '}
          instead.
        </TestInboxNote>
      )}
      <Button
        variant="outline"
        className="w-full"
        disabled={pending}
        onClick={() =>
          void run(async () => {
            setSent(false);
            await authApi.requestIdpInvitation(deliverTo ? { email, deliverTo } : { email });
            setSent(true);
          })
        }
      >
        {pending ? words.sending : words.again}
      </Button>
      <p role="status" className="mt-2 text-sm" data-sent-again>
        {sent ? words.sentAgain : ''}
      </p>
      {error !== null && (
        <p role="alert" className="mt-2 text-sm font-medium text-destructive">
          {errorMessage(error, 'Could not send it again.')}
        </p>
      )}
      <p className="mt-4 text-center text-sm text-muted-foreground">
        {words.wrongAddress} <HelpLink />
      </p>
    </>
  );
}

/**
 * Page 04, "You already have an account" — a person's run refused its number (`409
 * registration-refused`, `identifier_unavailable`): another account holds it (SR-487 decision 7).
 * survey-api ended the run, and the refusal itself queued an invitation to sign in for each
 * account that holds the number as its own, at its own address — `heldBy`, those addresses
 * masked (`j•••@domain`), which the notice names. **`[]` means nothing was sent** (the holder has
 * no `self` account, or the number was taken between two steps), so no notice is drawn, and the
 * no-access question goes with the address it asks about: the line says only that staff move the
 * account, with the Help link (Q25). Nothing is pressed to send it, and nothing here sends it
 * again: the wireframe has no such button. The number is the one as entered; at the completion's
 * re-check, where no number is in hand, the page says "That number".
 */
export function NumberTaken({
  number,
  heldBy,
  onRestart
}: {
  number: IEnteredNumber | undefined;
  heldBy: readonly string[];
  onRestart: () => void;
}) {
  const words = DOOR.taken;
  const named = number ? enteredNumberText(number, true) : words.unknownNumber;
  const sent = heldBy.length > 0;
  return (
    <>
      <StepHeading title={words.title} lede={words.lede(named)} />
      {sent && (
        <Alert className="mb-5" data-held-by>
          <AlertTitle>{words.noticeTitle}</AlertTitle>
          <AlertDescription>
            {words.onItsWay} <Addresses addresses={heldBy} />. {words.followLink}
          </AlertDescription>
        </Alert>
      )}
      <p className="mb-5 text-sm text-muted-foreground" data-move-account>
        {sent && <>{words.noAccess} </>}
        {words.moveAccount} <HelpLink />
      </p>
      <Button variant="outline" className="w-full" onClick={onRestart}>
        <span aria-hidden="true">← </span>
        {words.startAgain}
      </Button>
    </>
  );
}

/** The masked addresses, each in bold, "a, b and c". */
function Addresses({ addresses }: { addresses: readonly string[] }) {
  return (
    <>
      {addresses.map((address, index) => (
        <React.Fragment key={address}>
          {index > 0 && (index === addresses.length - 1 ? ' and ' : ', ')}
          <strong className="break-words">{address}</strong>
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * A firm's or a team's run refused, signed in (`409 registration-refused`,
 * `identifier_unavailable`): the number belongs to somebody who can already sign in, and the
 * number is the one thing this registrant cannot change. Wherever it is answered — the anchor,
 * the completion's re-check (two registrations of one number, the other a moment ahead), or a new
 * team's "Is this your team?" naming a team an account already acts for (SR-416) — survey-api
 * abandons the run with the refusal, `number_taken` beside it. Nothing this registrant can do
 * moves it, so the screen is one. A person's run ends on page 04 instead (`NumberTaken`).
 *
 * **It names nobody but the holder's addresses, masked.** Not "this CRD is already registered" —
 * the run id is printed in full as the reference: it is the one thing the registrant already
 * holds, it identifies no person, and it is what the staff queue is keyed on. The refusal itself
 * queued an invitation to sign in for the holder (PFR §2.4), so the notice says it went, to each
 * `heldBy` address, and nothing here sends it again (the owner's Q10). `[]` means nothing was
 * sent, so no notice is drawn; the heading, the reference and Help stay (Q25).
 */
export function NumberRefused({
  runId,
  heldBy,
  onRestart
}: {
  runId: string;
  heldBy: readonly string[];
  onRestart: () => void;
}) {
  return (
    <>
      <StepHeading
        title="We cannot continue with that number"
        lede="That number is not available for registration, so this registration has ended."
      />
      <dl className="mb-6 rounded-md border border-border bg-[var(--color-slight-blue)]/30 p-4">
        <dt className="text-xs text-muted-foreground">Reference</dt>
        <dd className="mt-1 font-mono text-xs break-all select-all">{runId}</dd>
      </dl>
      {heldBy.length > 0 && (
        <Alert className="mb-6" data-held-by>
          <AlertDescription>
            We have sent an invitation to sign in to the account that holds it — it is on its way
            to <Addresses addresses={heldBy} />.
          </AlertDescription>
        </Alert>
      )}
      <Button variant="outline" className="w-full" onClick={onRestart}>
        Start again
      </Button>
      <HelpLine />
    </>
  );
}

/** `409 registration-out-of-step`, run `completed`: the account exists — this tab, or
 *  another, already finished the run — so the one thing left is to sign in. Signed in, the run
 *  was the account's own, and what it added is already in the account's surveys. */
export function RunFinished({ caller }: { caller: RegistrationCaller }) {
  if (caller === 'account') {
    return (
      <>
        <StepHeading
          title="This registration is finished"
          lede="What it added is already on your account."
        />
        <Button asChild className="w-full">
          <Link to={CATALOGUE_ROUTE}>Go to your surveys</Link>
        </Button>
      </>
    );
  }

  return (
    <>
      <StepHeading
        title="This registration is finished"
        lede="The account it created is ready. Sign in at Invent with the address registered."
      />
      <Button asChild className="w-full">
        <Link to={LOGIN_ROUTE}>Sign in</Link>
      </Button>
    </>
  );
}

/** `409 registration-out-of-step`, run `abandoned`: cancelled, refused, or ended by
 *  survey-api's seven-day sweep — all one answer on the wire, so all one screen here. */
export function RunClosed({ onRestart }: { onRestart: () => void }) {
  return (
    <>
      <StepHeading
        title="This registration was closed"
        lede="It was cancelled, or left unfinished for too long, and cannot be picked up again."
      />
      <Button className="w-full" onClick={onRestart}>
        Start a new registration
      </Button>
      <HelpLine />
    </>
  );
}

/** The way to Help from an end that is not an account — a link, never a button: the screens
 *  around it are counted by their buttons. */
function HelpLine() {
  return (
    <p className="mt-6 text-center text-sm text-muted-foreground">
      <HelpLink />
    </p>
  );
}
